import { AnalyticsEvents, PaymentMethod, useTranslation } from "@next-platform/client-common-core";
import { RadioGroup, RadioGroupProps as NextUIRadioGroupProps } from "@nextui-org/react";
import { forwardRef, useEffect } from "react";

import { BASE_PAYMENT_METHOD_CLASS } from "@/constants";
import useQueueLogEvent from "@/hooks/useQueueLogEvent";
import { getPaymentMethodConfig } from "@/utils/subscription";

import { cn } from "../../utils/cn";
import PaymentIcon from "./PaymentIcon";
import PaymentMethodRadio from "./PaymentMethodRadio";

// Extend the IRadioGroupProps to include paymentMethods
interface IRadioGroupProps extends NextUIRadioGroupProps {
  paymentMethods: string[];
}

const PaymentMethodRadioGroup = forwardRef<HTMLDivElement, IRadioGroupProps>(({ className, classNames, value, paymentMethods, ...props }, ref) => {
  const { t } = useTranslation();
  const { logEvent } = useQueueLogEvent();
  const paymentMethodConfig = getPaymentMethodConfig(t);

  const logCardSelection = (payment_method: string) => {
    logEvent(AnalyticsEvents.CHECKOUT_PAYMENT_METHOD_SELECT, { payment_method });
  };

  useEffect(() => {
    if (value) logCardSelection(value as string);
  }, [value]);

  const supportedMethods = paymentMethods.filter((method): method is PaymentMethod => (method as PaymentMethod) in paymentMethodConfig);

  return (
    <RadioGroup
      {...props}
      value={value}
      ref={ref}
      classNames={{
        base: cn("mt-4 mb-2", classNames?.base, className),
        wrapper: cn("grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-3", classNames?.wrapper),
      }}
      label={t("subscription:checkout.paymentMethods")}
      orientation="horizontal"
    >
      {supportedMethods.map((method) => {
        const cfg = paymentMethodConfig[method]!;
        return (
          <PaymentMethodRadio
            key={method}
            className={cfg.className ?? BASE_PAYMENT_METHOD_CLASS}
            description={cfg.description}
            icon={<PaymentIcon {...cfg.icon} />}
            label={cfg.label}
            value={method}
          />
        );
      })}
    </RadioGroup>
  );
});

PaymentMethodRadioGroup.displayName = "PaymentMethodRadioGroup";

export default PaymentMethodRadioGroup;
